import {TraceApiEndpointBuilder} from '../traceApi';
import {IDeviceInfo} from './sessions.endpoint';

// Vodacom Check types
export interface VodacomCheckRequest {
  productId?: string; // Optional - backend uses its own config by default
  msisdn?: string;
  emsisdn?: string;
}

export interface VodacomCheckResponseAllowLogin {
  action: 'allow_login';
  token: string;
}

export interface VodacomCheckResponseRedirect {
  action: 'redirect_to_penrose';
  reason?: string;
  url?: string;
}

export type VodacomCheckResponse =
  | VodacomCheckResponseAllowLogin
  | VodacomCheckResponseRedirect;

// Vodacom Login types
export interface VodacomLoginRequest {
  checkToken: string;
  deviceInfo?: IDeviceInfo;
  lang?: 'pt-BR' | 'fr' | 'en-US';
}

export interface VodacomLoginResponse {
  accessToken: string;
  user: {
    id: string | null;
    carrier: string;
    lang: string;
  };
  session?: {
    _id: string;
    userId: string;
    deviceInfo?: IDeviceInfo;
    createdAt: string;
    lastActivity: string;
    expiresAt: string;
  };
}

// Vodacom OTP types
export interface VodacomRequestOtpRequest {
  msisdn: string;
}

export interface VodacomRequestOtpResponse {
  action: 'otp_sent';
}

export interface VodacomSubmitOtpRequest {
  msisdn: string;
  otp: string;
}

export interface VodacomSubmitOtpResponse {
  action: 'allow_login';
  accessToken: string;
  user: {
    id: string | null;
    carrier: string;
    lang: string;
  };
  subscriptionStatus: 'Active' | 'Pending' | 'Interested' | 'Suspended';
  encryptedMsisdn?: string;
  subscribeUrl?: string;
}

// Vodacom Refresh types
export interface VodacomRefreshResponse {
  action: 'refreshed' | 'logout' | 'redirect_to_penrose';
  reason?: string;
  url?: string;
}

export const createVodacomEndpoint = (builder: TraceApiEndpointBuilder) => ({
  // Check Vodacom subscription status and get login token
  vodacomCheck: builder.mutation<VodacomCheckResponse, VodacomCheckRequest>({
    query: body => ({
      url: '/auth/vodacom/check',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Login with Vodacom check token
  vodacomLogin: builder.mutation<VodacomLoginResponse, VodacomLoginRequest>({
    query: body => ({
      url: '/auth/vodacom/login',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Request OTP for Vodacom MSISDN login
  vodacomRequestOtp: builder.mutation<
    VodacomRequestOtpResponse,
    VodacomRequestOtpRequest
  >({
    query: body => ({
      url: '/auth/vodacom/request-otp',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Submit OTP and authenticate
  vodacomSubmitOtp: builder.mutation<
    VodacomSubmitOtpResponse,
    VodacomSubmitOtpRequest
  >({
    query: body => ({
      url: '/auth/vodacom/submit-otp',
      method: 'POST',
      body,
      noAuth: true,
      skipAuthGuard: true,
    }),
  }),

  // Refresh Vodacom subscription (backend reads claims from Firebase)
  vodacomRefresh: builder.mutation<VodacomRefreshResponse, void>({
    query: () => ({
      url: '/auth/vodacom/refresh',
      method: 'POST',
      body: {},
      skipAuthGuard: true,
    }),
  }),
});
